/**
 * src/shell/shell.js
 *
 * App shell: A1 portal IA (CK-approved mockup, ia_layouts.html LAYOUT A1,
 * 2026-07-12) — a LABELED left rail of 5 pillars (People / Money /
 * Operations / Compliance · KSA (gold) / Insights & Tools, see
 * shell/pillars.js) each opening onto a per-pillar SPRINGBOARD home
 * (greeting + Quick Actions + Apps tile grid, see shell/pillar_home.js),
 * plus the top bar (notifications link, LTR/RTL toggle, role badge, account
 * menu) and a routed content region. Replaces the prior six-collapsible-
 * group sidebar. Navigation uses the hash router (src/lib/router.js) so
 * every screen — and every pillar springboard — is a real routed view and
 * the browser back button works.
 */

import { signOut } from 'firebase/auth';
import { auth } from '../lib/firebase.js';
import { getBootstrap, resetBootstrapCache } from '../lib/session-store.js';
import { registerRoute, onRouteChange, navigate, startRouter, getCurrentPath, splitPath } from '../lib/router.js';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase.js';
import { callFn, guardedRead } from '../lib/api.js';
import { updateConnectionStatus, getConnectionBadgeText } from '../lib/connection-status.js';
import { getLang, setLang, addLangListener } from '../i18n/store.js';
import { t } from '../i18n/t.js';
import {
  ICONS,
  ALL_SCREENS,
  isItemVisible,
  visiblePillars,
  landingPathFor,
  pillarOwning,
  pillarIdFromPath,
} from './pillars.js';
import { mountPillarHome, recordRecentVisit, roleLabel } from './pillar_home.js';
import { mountPlaceholder } from '../screens/placeholder.js';
// PERF (2026-07-19, route-level code-splitting): every screen mounter below
// used to be a static `import { mountX } from '../screens/x.js'` here, which
// pulled all ~97 screen modules into one 3.7MB render-blocking bundle on
// every visit. Each is now a dynamic `import()` inline in SCREEN_MOUNTERS
// (below) so Rollup gives every screen its own chunk, loaded only on first
// navigation to that route. The per-wave history notes that used to sit next
// to each screen's static import are kept here (now orphaned from their
// import line, but still the record of why/when each screen was wired in —
// see SCREEN_MOUNTERS for the actual per-path dynamic-import wiring).
// Admin decision boards (built 857f24eb, registered in this integration wave):
// hr_requests_admin/grievances_admin were committed unwired — see each
// screen's own header comment for the suggested-path spec this follows.
// Approval Routing (built dbe21085, registered in this integration wave —
// closes worklist gap #7; see that screen's header for the CF contract +
// the READ-CF GAP it documents).
// Org Chart / Tenant Config (2026-07-15 integration wave — built unwired,
// registered here + in pillars.js's People / Insights & Tools pillars).
// Wave 5 (2026-07-13) — vertical read-only screens (construction / hospitality
// / manufacturing). Each backed by an entitlement-fail-closed read-CF.
// Integration wave (2026-07-15, shared-file integrator pass): four screens
// built + committed but unwired — tickets/workstreams/journeys/crm. Each is
// a COLD ZONE wiring of an EXISTING sole-writer CF (see each screen's own
// header for its CF contract + any read-CF gap); zero new CFs/functions/
// rules in this pass. Registered here + in shell/pillars.js's nav lists.
// Shared-file integrator pass (2026-07-15): two more screens built +
// committed but unwired — manufacturing work-orders/work-centers/routings
// (COLD ZONE wiring of the existing `manufacturingMutate` CF) and
// maintenance/FM (COLD ZONE wiring of the existing `maintenanceMutate` CF).
// Zero new CFs/functions/rules in this pass. See each screen's own header
// for its CF contract + read-CF gap. Registered here + in
// shell/pillars.js's Verticals (manufacturing) and Operations pillars.
// Shared-file integrator pass (2026-07-15, continued): fixed_assets screen
// built + committed but unwired — COLD ZONE wiring of the existing
// `fixedAssetMutate` CF (fixed_assets + depreciation_ledger already
// server-only, FLAG-A CK-ratified 2026-06-29). Zero new CFs/functions/rules
// in this pass. See screens/fixed_assets.js's own header for the full CF
// contract + DC-05 money-safety note. Registered here + in
// shell/pillars.js's Money pillar.
// Shared-file integrator pass (2026-07-15, continued): AR + AP cockpit
// screens built + committed but unwired — COLD ZONE wiring of EXISTING
// arap_callables.ts (AR) / procurementMutate + arap_callables.ts (AP). Zero
// new CFs/functions/rules in this pass. See each screen's own header for
// its CF contract + read-CF gaps. Registered here + in shell/pillars.js's
// Money pillar (directly after '/sales-orders').
// Shared-file integrator pass (2026-07-16, continued): Muqeem / Iqama
// Expiry board — COLD ZONE wiring of the EXISTING three Muqeem action CFs
// (functions/src/muqeem/muqeem_callables.ts) plus the new
// getMuqeemExpiryBoard read-CF (committed, not yet deployed — the screen
// fails soft to its gap banner until it ships). Zero new CFs/functions/
// rules in this pass. See screens/muqeem_iqama.js header for the full CF
// contract. Registered here + in shell/pillars.js's Compliance · KSA
// pillar (directly after '/nitaqat').
// Shared-file integrator pass (2026-07-16, continued): Offboarding /
// Terminations board — COLD ZONE wiring of the EXISTING journeyMutate CF
// (functions/src/journeys/journey_mutate_service.ts, same 4 ops journeys.js
// already wires: instantiate/completeStep/skipStep/cancelJourney), fixed to
// templateKey 'offboarding_standard'. Zero new CFs/functions/rules in this
// pass. See screens/offboarding.js header for the full route-reg spec + the
// honest employee-status gap (completing this checklist does NOT flip the
// employee doc's status — that's the separate, older termination_requests
// flow). Registered here + in shell/pillars.js's People pillar (directly
// after '/journeys').
// Wave-1 integration pass (2026-07-16): four screens built + committed but
// unwired — messages/recurring-payroll/attendance-config/offer-letters.
// Zero new CFs/functions/rules in this pass (messages, recurring-payroll,
// and attendance-config's 4 non-biometric tabs are direct-Firestore per
// their own headers; offer-letters wires the existing downloadOfferLetterPdf
// CF). Registered here + in shell/pillars.js's People / Money / Operations
// pillars. leave_exemptions.js was DELIBERATELY unregistered while it was a
// "no backend yet" stub; its CF pair (getWorkingHoursExemptions /
// exemptionMutate) shipped 2026-08-07 and the screen is now registered at
// '/leave-exemptions' (People pillar, ROLE_ADMIN_HR — see SCREEN_MOUNTERS).
// Wave-2 integration pass (2026-07-16): six screens built + committed but
// unwired — Finance ledgers (trial_balance/cash_ledger, W2-F, WEB_PARITY gap
// #31) + Hospitality depth (hospitality_rooms/hospitality_deposits/
// hospitality_tax/leases, W2-F, WEB_PARITY gap #33). Zero new CFs/functions/
// rules in this pass — every screen is an action/read console over EXISTING
// callables (getTrialBalance / cashLedgerMutate / hospitalityMutate /
// processHospitalityDeposit / processHospitalityTaxRemittance / leaseMutate).
// See each screen's own header for its full CF contract + honest read-CF
// gaps. Registered here + in shell/pillars.js's Money (trial-balance,
// cash-ledger) and Verticals (the hospitality trio + leases) pillars.
// Launch-critical + fast-follow integrator pass (2026-07-17) — 7 screens
// built + committed but unwired: Privacy/DSAR (PDPL statutory), Billing &
// Subscription, Policy Hub (ROLE_OPEN — every tenant member must
// acknowledge), Recycle Bin, Calendar Export (ROLE_OPEN self-service), ID
// Cards, Request-Type/SLA Config. Each uses the callable-fallback shim
// (lib/api.js guardedRead) so it works pre/post any new CF deploy. See each
// screen's own header for its full CF/collection contract. Registered here +
// in shell/pillars.js (Compliance·KSA / Insights & Tools / People /
// Operations, per each screen's role gate).
// FIX 3 (2026-07-11): mountAlertRules intentionally NOT in the tenant
// rail/palette — Alert Rules is PLATFORM-GLOBAL data (see the Insights &
// Tools pillar comment in pillars.js). 2026-08-07: it is now deliberately
// reachable on the PLATFORM tier via the hidden '/platform-alerts' route
// (registered below, outside ALL_SCREENS like '/platform') from the
// superadmin screen's Alert Rules card — every alert-rules CF re-gates
// requirePlatformRole server-side regardless.
import { mountCommandPalette, openCommandPalette } from './command_palette.js';
// Entitlement-aware nav (2026-08-07): one session-wide one-shot read of
// companies/{tid}/entitlements/current, resolved BEFORE the cockpit frame
// mounts so the first rail render is already module-filtered. NEVER rejects,
// bounded at 8s, fail-open — see lib/entitlements-store.js header.
import { loadEntitlements } from '../lib/entitlements-store.js';
// Persona fork (EMPLOYEE_PORTAL_PLAN §1, 2026-07-14): employee/supervisor
// callers get a separate self-service shell that lives entirely in its own
// module (own IA, own routes, own screens) so this file stays single-owner for
// the admin cockpit. The import is used only inside attemptBootstrapAndMount
// (runtime), so the shell.js↔employee_shell.js circular edge is import-safe.
import { mountEmployeeShellFrame } from './employee_shell.js';
// Interactive hex-grid identity backdrop (DESIGN_SPEC §1.1, CK-flagged
// 2026-07-15) — mounted once here since mountShell() is the single entry
// point BOTH personas (admin cockpit + employee/supervisor portal) and the
// loading/unauthorized interstitials all flow through before forking; see
// hex_backdrop.js header for why the prior CSS-only lattice was invisible.
import { mountHexBackdrop } from './hex_backdrop.js';

/** Branding variant key used for the compact/header logo — mirrors
 * LogoVariant.compact.key in lib/domain/models/tenant_branding.dart (the
 * on-disk contract; MUST NOT drift from the Flutter substrate). */
const LOGO_VARIANT_KEY = 'compact';

/**
 * Persona routing (EMPLOYEE_PORTAL_PLAN §1, 2026-07-14). Two tenant-member
 * surfaces now share this single entry point, forked on the resolved role:
 *  - ADMIN_COCKPIT_ROLES → the existing 5-pillar admin cockpit
 *    (mountShellFrame, UNCHANGED). 'finance' is NEWLY added — its backing
 *    read-CFs already admit finance server-side; every cockpit behaviour flows
 *    through visiblePillars(role)/isItemVisible untouched (the finance pillar
 *    re-tags themselves live in pillars.js, a separate wave).
 *  - EMPLOYEE_PORTAL_ROLES → the NEW self-service employee shell
 *    (mountEmployeeShellFrame, employee_shell.js). 'supervisor' rides with
 *    'employee' here — approver-class differences are resolved inside getMyWork
 *    server-side, not by a separate surface (plan §5.2).
 * Any other definitively-resolved role still lands on unauthorizedTemplate().
 * Role vocabulary mirrors firestore.rules verbatim.
 * 'support_agent' added 2026-08-07: pillars.js's '/tickets' item already
 * named it in its roles Set, but this shell admit-list routed the role to
 * unauthorizedTemplate() so it could never reach the cockpit at all. A
 * support_agent now lands in the cockpit and isItemVisible narrows their
 * rail to Service Desk + ROLE_OPEN items; getSupportTickets re-gates
 * server-side regardless. */
const ADMIN_COCKPIT_ROLES = new Set(['admin', 'hr', 'manager', 'finance', 'support_agent']);
const EMPLOYEE_PORTAL_ROLES = new Set(['employee', 'supervisor']);

/**
 * Per-item nav role matrix (2026-07-11 role-nav fix wave, root cause of the
 * "manager lands on a screen whose read-CF unconditionally denies them"
 * class — e.g. a manager defaulting to /dashboard, whose getDashboardSummary
 * throws permission-denied for anything but admin|hr) — now lives in
 * shell/pillars.js (each pillar's `screens[].roles`), carried over verbatim
 * by the 2026-07-12 A1 IA rebuild. Every screen carries a `roles` field:
 *   - a Set of {'admin','hr','manager'} for an item whose backing read-CF
 *     denies at least one of those three roles outright (grep-verified
 *     against the actual CF role gate — see the per-item comment in
 *     pillars.js);
 *   - `null` (ROLE_OPEN) for an item whose backing read-CF has NO role
 *     restriction beyond tenant membership — it may self/branch-scope the
 *     result for a non-privileged caller, but it never throws
 *     permission-denied purely on role, so it's safe to show to any
 *     resolved caller AND to the narrowed fail-open fallback (role===null,
 *     see mountShell's fail-open note + isItemVisible in pillars.js).
 * Two entries are a deliberate AGREED WIDENING beyond what the CF alone
 * would allow (Leave, Audit Log) — see their inline comments in pillars.js;
 * both were explicitly called out by the fix brief as the agreed matrix,
 * not something this pass discovered independently.
 */
/**
 * mountShell — the role gate. Renders a lightweight loading state (no nav,
 * no routes, no screen callables issued) until getSessionBootstrap resolves,
 * reads the caller's role off the SAME bootstrap payload the role badge
 * already uses (data.profile.role — see populateUserChip below), and only
 * then either mounts the full shell (admin/hr/manager) or a polite
 * full-screen "wrong surface" state (every other role). This blocks the
 * shell's screens from ever issuing a single callable for a caller who
 * isn't allowed to be here.
 *
 * Fail-open on an UNKNOWN role (bootstrap timed out / errored / no profile
 * doc yet): this is a UI convenience gate, not the security boundary — every
 * *Mutate/read CF and firestore.rules re-checks role server-side regardless
 * — and getBootstrap's whole reason for existing is the 503-graceful-
 * degradation UX (see session-store.js header); blocking a legitimate
 * admin/hr/manager from ever reaching the shell during a backend hiccup
 * would be a regression of that. Only a DEFINITIVELY KNOWN non-admin/hr/
 * manager role blocks.
 *
 * FIX 4 (P2 defense, 2026-07-11): the unknown-role fail-open case now
 * passes role=null through to mountShellFrame, which renders a NARROWER
 * rail (only pillars carrying at least one ROLE_OPEN screen — see
 * isItemVisible/visiblePillars in pillars.js) instead of the full
 * admin/hr/manager chrome, reducing what's on screen during a
 * token-refresh race. CFs remain the real boundary either way; this is
 * chrome-exposure reduction only, not a new enforcement layer.
 *
 * FIX (2026-07-12, session-bootstrap-strand): a bootstrap that outright
 * FAILS (timed out, or resolved with no data at all — as opposed to
 * resolving successfully with no profile doc, which is a definitive state)
 * now gets ONE bounded automatic retry via attemptBootstrapAndMount before
 * falling back to the fail-open role=null render, because session-store.js
 * no longer permanently caches a failed bootstrap — a routine me-central2
 * cold-start timeout on the FIRST call used to poison the cache forever,
 * stranding the whole session behind the narrowed ROLE_OPEN-only nav with
 * no way back (the retry that could fix it, resetBootstrapCache(), lived on
 * the now-unreachable Dashboard screen). If the bounded retry also fails,
 * mountShellFrame still renders the same fail-open shell as before, plus a
 * small manual "Retry" banner (bootstrapFailed=true) so the admin has a way
 * out that doesn't require a hard refresh.
 */
export function mountShell(root, user) {
  mountHexBackdrop(); // idempotent — no-ops if login.js already mounted it this page load
  root.innerHTML = shellLoadingTemplate();
  attemptBootstrapAndMount(root, user, 0);
}

/** Bounded so a persistently-down backend degrades to the fail-open shell
 * (with its own manual retry banner) instead of retrying forever. */
const MAX_BOOTSTRAP_RETRIES = 1;
const BOOTSTRAP_RETRY_DELAY_MS = 1500;

/**
 * isPlatformAccount — POSITIVE platform-tier identification off the caller's
 * own ID token custom claims: `stafflyAdmin === true` (legacy) OR
 * `tier === 'platform'` — the EXACT same predicate the server enforces in
 * functions/src/platform/auth/platform_rbac.ts (requirePlatformRole, L307)
 * and the Flutter app resolves in lib/main.dart
 * (_forceResolvePlatformAdminClaim, L3098). Uses the CACHED token (no
 * forced refresh — this is a routing convenience, not an enforcement gate;
 * the Superadmin Portal re-verifies server-side regardless). Fail-closed to
 * `false` on any error so a token hiccup can never divert a normal tenant
 * caller away from the shell.
 */
function isPlatformAccount(user) {
  return user
    .getIdTokenResult()
    .then((res) => {
      const claims = (res && res.claims) || {};
      return claims.stafflyAdmin === true || claims.tier === 'platform';
    })
    .catch(() => false);
}

/** 2026-07-24 fix (Q11 commercial-minimum / superadmin-hidden-entry): the
 * external `zephyr-ops-7q3k` Flutter Superadmin Portal this constant used to
 * point at was NEVER SHIPPED (see screens/superadmin.js's file header) —
 * the platform-account interstitial below (written 2026-07-15) still linked
 * to it as a dead end even after the in-app replacement (screens/
 * superadmin.js, route /platform) shipped on 2026-07-21. Since #content-region
 * and the router only exist once mountShellFrame() has run, and the
 * interstitial branch above returns BEFORE ever calling mountShellFrame for a
 * platform-tier caller, the built replacement screen was 100% unreachable —
 * no live path led to it. Fixed by wiring the interstitial's button to
 * mountShellFrame + navigate('/platform') instead (see
 * wirePlatformInterstitial) — this constant is no longer referenced. */

function attemptBootstrapAndMount(root, user, retryCount) {
  getBootstrap().then((result) => {
    const { data, timedOut } = result;
    // A bootstrap that truly failed (timed out, or no data reached us at
    // all) is distinct from one that succeeded but found no profile doc —
    // only the former is worth an automatic retry; the latter is a
    // definitive answer that retrying cannot change.
    const bootstrapFailed = timedOut || !data;
    const role = data && data.profileExists && data.profile ? data.profile.role : null;

    // Persona fork (plan §1.2): a DEFINITIVELY-resolved employee/supervisor
    // gets the new self-service shell. Both checks below require a KNOWN role —
    // a failed/unresolved bootstrap (role===null) falls straight through to the
    // retry + fail-open admin chrome below, exactly as it did before (an
    // employee whose bootstrap failed lands on the narrowed ROLE_OPEN nav, an
    // acceptable degraded state — plan §1.1, deliberately not re-litigated).
    if (role && EMPLOYEE_PORTAL_ROLES.has(role)) {
      mountEmployeeShellFrame(root, user, role, bootstrapFailed);
      return;
    }

    if (role && !ADMIN_COCKPIT_ROLES.has(role)) {
      root.innerHTML = unauthorizedTemplate();
      wireUnauthorizedSignOut(root);
      return;
    }

    if (bootstrapFailed && retryCount < MAX_BOOTSTRAP_RETRIES) {
      setTimeout(() => {
        // session-store.js's getBootstrap() no longer memoizes a failed
        // result on its own, but resetBootstrapCache() here is the explicit
        // belt-and-suspenders guarantee this retry issues a REAL fresh
        // network call rather than depending on that implementation detail.
        resetBootstrapCache();
        attemptBootstrapAndMount(root, user, retryCount + 1);
      }, BOOTSTRAP_RETRY_DELAY_MS);
      return;
    }

    // Platform-account interstitial (2026-07-15): a caller whose ID token
    // POSITIVELY carries the platform-tier claim (stafflyAdmin===true or
    // tier==='platform' — see isPlatformAccount) has no tenant role here by
    // design, so instead of the stripped role=null cockpit they get a clean
    // branded hand-off to the Superadmin Portal. Checked ONLY on this final
    // role===null fall-through: a definitively-resolved tenant role above
    // (cockpit / employee portal / unauthorized) is never diverted, and a
    // non-platform caller falls through to the EXACT same fail-open shell
    // as before (isPlatformAccount fail-closes to false on any token error,
    // so the transient-bootstrap-failure path is behaviorally unchanged).
    isPlatformAccount(user).then((isPlatform) => {
      if (isPlatform) {
        root.innerHTML = platformAccountTemplate();
        wirePlatformInterstitial(root, user);
        return;
      }
      // timedOut/error (retries exhausted) / no-profile-doc / unknown role →
      // fall through and mount the shell (see fail-open rationale above) with
      // role=null, which renders the FIX-4 narrowed nav (ROLE_OPEN items
      // only — see isItemVisible); populateUserChip will show 'offline'/
      // 'limited' in that case, exactly as it already does today.
      // Entitlement pre-load (2026-08-07): resolve the one-shot
      // entitlements/current read BEFORE the first rail render so module
      // gating applies from the very first paint. loadEntitlements never
      // rejects and is bounded at 8s inside guardedRead — mount can never
      // strand; a failed/absent read renders the un-gated rail (fail-open).
      loadEntitlements(data && data.companyId ? data.companyId : null).then(() => {
        mountShellFrame(root, user, role, bootstrapFailed);
      });
    });
  });
}

function mountShellFrame(root, user, role, bootstrapFailed = false) {
  root.innerHTML = shellTemplate();

  if (bootstrapFailed) {
    mountDegradedSessionBanner(root);
  }

  // Impersonation banner (2026-08-20, platform-auth-console lane) — an
  // active impersonation grant must be unmissable while it lasts, on EVERY
  // route, not just the console that minted it (an operator's browsing
  // session is not confined to one screen). Fire-and-forget, mirrors
  // mountDegradedSessionBanner immediately above (same #app-shell.prepend()
  // mechanism, the established "always-visible regardless of route" seam in
  // this file) — reads the SAME token.impersonation claim
  // adminMintImpersonationGrant sets and firestore.rules / this codebase's
  // assertNotReadOnlyImpersonation already trust; no new CF, no rules edit.
  mountImpersonationBanner(root, user);

  // pillars = the role-filtered A1 pillar list (rail + springboards + Cmd+K
  // all read from this ONE computed value, so they can never drift from one
  // another — mirrors the old FIX 1 guarantee). Pillars with zero visible
  // screens for this role are already dropped by visiblePillars.
  const pillars = visiblePillars(role);
  renderSidebarNav(root, pillars);

  // Mobile sidebar open/close.
  const sidebarEl = root.querySelector('.sidebar');
  const overlayEl = root.querySelector('#sidebar-overlay');
  root.querySelector('#menu-btn').addEventListener('click', () => {
    sidebarEl.classList.add('open');
    overlayEl.classList.add('visible');
  });
  const closeSidebar = () => closeMobileSidebar(root);
  root.querySelector('#sidebar-close').addEventListener('click', closeSidebar);
  overlayEl.addEventListener('click', closeSidebar);

  // Account menu (avatar → Profile / Security / Sign out) — replaces the
  // native window.confirm() dialog with a real dropdown.
  wireAccountMenu(root);

  // Notification bell is an honest link to the routed Notifications screen
  // (a real, if still-placeholder, destination) — not a hard-coded "always
  // on" dot with no feed behind it. The count badge is driven by the SAME
  // getNotifications read the Notifications screen uses (guarded, fail-silent,
  // hidden when unread === 0).
  root.querySelector('#header-notif-btn')?.addEventListener('click', () => navigate('/notifications'));
  loadNotifCount(root);

  // Global search box (top bar) — a visible affordance that opens the
  // existing Cmd/Ctrl+K command palette (discoverability only; the palette
  // owns the actual search).
  wireHeaderSearch(root);

  // Hidden Platform Ops entry (2026-07-21) — a double-click on the sidebar
  // brand logo, gated to platform-tier callers only (see
  // wireSuperadminEntry). Deliberately NOT a visible affordance.
  wireSuperadminEntry(root, user);

  // Interface direction (LTR/RTL) toggle.
  wireLangToggle(root);

  // Light/dark theme toggle (activates the rose-light theme).
  wireThemeToggle(root);

  // Build a local path→mount-fn lookup (routeTable) with the EXACT same
  // entries this loop always registered on the router — kept as a local
  // Map (rather than only calling registerRoute) so the language-toggle
  // re-render below (reRenderActiveRoute) can look up and re-invoke the
  // current route's mount function without inventing a second router or
  // duplicating router.js's private render() lookup logic.

  // Register a route for EVERY screen in the FULL (unfiltered) inventory —
  // not just this role's visible ones — mirroring the old ALL_ITEMS
  // registration: nav-level filtering is a UI convenience only, CFs remain
  // the real security boundary (a hidden screen reached by a stale/typed
  // URL still 403s server-side rather than crashing client-side).
  const routeTable = new Map();
  for (const item of ALL_SCREENS) {
    const mount = SCREEN_MOUNTERS[item.path];
    routeTable.set(item.path, mount || ((el) => mountPlaceholder(el, screenLabel(item))));
  }

  // One springboard route per VISIBLE pillar (/pillar/{id}) — the A1 "home"
  // for that pillar (greeting + Quick Actions + Apps grid, pillar_home.js).
  // Only visible pillars get a route: a role with zero visible screens in a
  // pillar has no reason to reach its springboard either.
  for (const pillar of pillars) {
    routeTable.set(`/pillar/${pillar.id}`, (el) => mountPillarHome(el, pillar));
  }

  // Detail screens accessed via query params (e.g. /customer-statement?partyRef=...&from=...&to=...)
  routeTable.set('/customer-statement', async (el) => (await import('../screens/customer_statement.js')).mountCustomerStatement(el));

  // Hidden Platform Ops screen (2026-07-21) — reachable ONLY via a
  // double-click on the sidebar brand logo (wireSuperadminEntry, above) for
  // a platform-tier caller. Deliberately NOT in shell/pillars.js's
  // ALL_SCREENS, so it never appears in the rail, command palette, or any
  // springboard — this is the one route registered outside that loop for
  // that reason. screens/superadmin.js independently re-checks the
  // platform-tier claim at mount (a stale/typed #/platform hash from a
  // tenant-only caller sees an access-denied card, never a crash), and
  // every CF it calls re-gates server-side via requirePlatformRole
  // regardless — see that file's header for the full security note.
  routeTable.set('/platform', async (el) => (await import('../screens/superadmin.js')).mountSuperadmin(el, user));

  // Platform Alert Rules (2026-08-07) — platform-tier only (get_alert_rules.ts
  // + every alertRule* CF re-gate requirePlatformRole server-side). Reached
  // from the superadmin screen's Alert Rules card; deliberately outside
  // ALL_SCREENS like '/platform' itself — never rail/palette/springboard.
  // Supersedes FIX 3 (2026-07-11)'s unwiring: the tenant shell still never
  // offers it; only the platform surface links here. mountAlertRules(el, ctx)
  // ignores ctx (alert_rules.js:89).
  routeTable.set('/platform-alerts', async (el) => (await import('../screens/alert_rules.js')).mountAlertRules(el));

  // Platform Integrations (2026-08-19, GATE 51 callable-has-caller closure) —
  // platform-tier only. adapterRegister/adapterSetEnabled/apiKeyIssue/
  // apiKeyRotate/apiKeyRevoke/webhookRegister/webhookRevoke
  // (functions/src/admin/platform_integration_callables.ts) all re-gate
  // requirePlatformRole server-side. Same outside-ALL_SCREENS pattern as
  // '/platform'/'/platform-alerts' above — never rail/palette/springboard.
  // screens/superadmin.js (owned by another lane) has no "Open Integrations"
  // card yet — reachable today by typing #/platform-integrations, same as
  // '/platform-alerts' was before its card existed. See
  // screens/platform_integrations.js header for the full CF + read contract.
  routeTable.set('/platform-integrations', async (el) => (await import('../screens/platform_integrations.js')).mountPlatformIntegrations(el));

  // Platform DSAR — export (2026-08-19) AND erasure (2026-08-19/20, once the
  // two server guards below were fixed) — platform-tier only. adminDsarExport
  // / adminDsarErase (functions/src/admin/dsar_*.ts) re-gate
  // requirePlatformRole(ctx,'manage_dsar') server-side; this screen
  // additionally duplicates the isPlatformAccount() client-side pre-check
  // (defense-in-depth — this screen touches real PII/PHI). Same
  // outside-ALL_SCREENS pattern as '/platform'/'/platform-integrations' —
  // never rail/palette/springboard. screens/superadmin.js (owned by another
  // lane) has no "Open DSAR" card yet — reachable today by typing
  // #/platform-dsar. adminDsarErase was DELIBERATELY NOT wired until
  // dsar_erase.ts gained a subject-to-tenant binding check
  // (assertSubjectBelongsToTenant) and a second-approver break-glass gate
  // (assertBreakGlassApproved, operation 'dsar.erase') — see
  // screens/platform_dsar.js's header + .scratch/lane_dsar_erase_2026-08-19.md.
  routeTable.set('/platform-dsar', async (el) => (await import('../screens/platform_dsar.js')).mountPlatformDsar(el));

  // Platform Onboarding Wizard (2026-08-19, GATE 51 callable-has-caller
  // closure) — platform-tier only. adminOnboardTenant (functions/src/admin/
  // onboarding_wizard.ts) re-gates requirePlatformRole(ctx,'tenant.update')
  // server-side; screen duplicates isPlatformAccount() client-side
  // (defense-in-depth — this screen creates a real Firebase Auth user). Same
  // outside-ALL_SCREENS pattern as '/platform' above. screens/superadmin.js
  // (owned by another lane) already wires the two simpler primitives
  // (provisionTenant/provisionTenantAdmin) but explicitly documents
  // adminOnboardTenant's fuller wizard as NOT wired there — this IS that
  // fast-follow. Reachable today by typing #/platform-onboarding.
  routeTable.set('/platform-onboarding', async (el) => (await import('../screens/platform_onboarding.js')).mountPlatformOnboarding(el));

  // Platform Auth Console (2026-08-20, cluster 1 of GATE 51's unreachable-
  // callables packet, CK-ratified "approve all and proceed" — see
  // .scratch/PACKET_unreachable_callables_2026-08-19.md +
  // .scratch/RATIFICATION_unreachable_callables_2026-08-19.md) —
  // platform-tier only. Wires break-glass (adminRequestBreakGlass/
  // adminApproveBreakGlass/adminCancelBreakGlass), impersonation
  // (adminMintImpersonationGrant/adminRevokeImpersonationGrant/
  // adminExtendImpersonationGrant), platform-team (adminAddPlatformUser/
  // adminRemovePlatformUser/adminReRolePlatformUser), migrations
  // (adminRunMigration/adminRollbackMigration) and config rollback
  // (adminRollbackConfig) — all nine-plus-three re-gate requirePlatformRole
  // (+ requireRecentAuth step-up for destructive ops) server-side; no authz
  // was changed to wire this. Shares its break-glass call shape with
  // screens/platform_dsar.js's existing 'dsar.erase'-scoped mini-flow via
  // lib/breakglass.js (see that lib's + this screen's headers) — this is the
  // GENERAL console (any of the three registered operations), not a second,
  // drifting copy. Same outside-ALL_SCREENS pattern as every other
  // '/platform-*' route above — never rail/palette/springboard.
  // screens/superadmin.js (owned by another lane) has no "Open Auth Console"
  // card yet — reachable today by typing #/platform-auth-console, same
  // fast-follow note as this run's other new /platform-* screens.
  routeTable.set('/platform-auth-console', async (el) => (await import('../screens/platform_auth_console.js')).mountPlatformAuthConsole(el));

  // Hidden region-parity diagnostic (2026-07-22) — me-central2 cutover
  // pre-flip gate (REGION_ASK2_CLIENTFLIP_PACKET_2026-07-21.md). Same
  // outside-the-loop pattern as /platform above: reachable ONLY by typing
  // #/region-check, never in rail/palette/springboards. Both CFs it calls
  // (employeeMutate, revealEmployeePii) re-gate admin|hr + tenant claim
  // server-side; see screens/region_check.js header.
  routeTable.set('/region-check', async (el) => (await import('../screens/region_check.js')).mountRegionCheck(el));

  routeTable.set('__notfound__', (el) => mountPlaceholder(el, 'Not Found'));

  for (const [path, mountFn] of routeTable) registerRoute(path, mountFn);

  // LEAK FIX (2026-08-19): keep the unsub handle and drop the PREVIOUS
  // mount's listener first (mirrors _langListenerUnsub below) — router.js's
  // listener list is module-global and used to be append-only, so every
  // re-mount (sign-out → sign-in, platform interstitial → cockpit) stacked
  // one more closure retaining the old detached shell DOM via `root`.
  // main.js's resetRouteListeners() on auth transitions is the cross-shell
  // backstop; this handle covers same-shell re-mounts inside one auth state.
  if (_routeChangeUnsub) _routeChangeUnsub();
  _routeChangeUnsub = onRouteChange((path) => {
    updateActiveNav(root, path);
    recordScreenVisit(path);
  });

  // FIX 2 + FIX 4 (role-aware default landing, 2026-07-11; re-based onto
  // pillar springboards for the 2026-07-12 A1 IA): steps in on (a) a fresh
  // hash-less load (post-login) or (b) an existing hash that names a KNOWN
  // screen or pillar this role cannot see (most importantly a stale
  // #/dashboard hash left over from an earlier admin/hr session in the same
  // browser tab — getDashboardSummary unconditionally denies a manager).
  // Setting the hash BEFORE calling startRouter means startRouter's own
  // `if (!window.location.hash)` branch never fires with our value absent —
  // lib/router.js is untouched, this only pre-seeds what it reads.
  // Non-nav routes (e.g. /customer-statement, a detail screen reached via
  // query params) are left alone either way.
  const rawHash = window.location.hash;
  const currentPath = rawHash ? rawHash.slice(1) : '';
  const currentScreen = ALL_SCREENS.find((item) => item.path === currentPath);
  const currentPillarId = pillarIdFromPath(currentPath);
  const isKnownPillarPath = currentPillarId !== null;
  const currentPillarVisible = isKnownPillarPath && pillars.some((p) => p.id === currentPillarId);
  let needsRedirect = !rawHash;
  if (currentScreen && !isItemVisible(currentScreen, role)) needsRedirect = true;
  if (isKnownPillarPath && !currentPillarVisible) needsRedirect = true;
  if (needsRedirect) {
    window.location.hash = `#${landingPathFor(role)}`;
  }

  startRouter(root.querySelector('#content-region'));

  // Populate user chip / role badge asynchronously (never blocks the shell mount).
  populateUserChip(root, user);

  // Swap in the tenant logo asynchronously (never blocks the shell mount —
  // the STAFFLY wordmark renders immediately and stays put until/unless this
  // resolves with a usable image).
  populateTenantLogo(root);

  // Cmd/Ctrl+K command palette — reads the SAME role-filtered `pillars` the
  // rail rendered above (FIX 1: never offer a destination the rail itself
  // hides for this role), reshaped into the {label, items} groups
  // command_palette.js already expects (one group per pillar); owns its own
  // document-level keydown listener (AbortController-scoped, re-mount safe)
  // so it does not conflict with the account-menu Escape handler above.
  mountCommandPalette(paletteSections(pillars));

  // Language-change reactivity: re-render whatever is currently on screen
  // so t() re-evaluates with the new active language (feedback.js — the
  // i18n reference screen — is the concrete proof point) and refresh the
  // pillar labels this shell itself renders (rail + palette + page title).
  // Subscribed ONCE per mountShellFrame call via wireLangReactivity, which
  // unsubscribes any prior mount's listener first (mirrors the
  // _accountMenuAbort pattern above) so a sign-out/sign-in cycle never
  // stacks duplicate listeners/re-renders.
  wireLangReactivity(root, user, pillars, routeTable);
}

/** Path → real mount function for every screen this shell actually has a
 * built screen for (SIZE mirrors ALL_SCREENS in pillars.js exactly — a path
 * present in pillars.js but absent here falls through to the honest
 * mountPlaceholder stub in the registration loop above, never a crash). */
const SCREEN_MOUNTERS = {
  '/dashboard': async (el) => (await import('../screens/dashboard.js')).mountDashboard(el),
  '/attendance': async (el) => (await import('../screens/attendance.js')).mountAttendance(el),
  '/employees': async (el, query) => (await import('../screens/employees.js')).mountEmployees(el, query),
  '/leave': async (el) => (await import('../screens/leave.js')).mountLeave(el),
  '/leave-admin': async (el) => (await import('../screens/leave_admin.js')).mountLeaveAdmin(el),
  '/payroll': async (el) => (await import('../screens/payroll.js')).mountPayroll(el),
  '/payroll-runs': async (el, query) => (await import('../screens/payroll_runs.js')).mountPayrollRuns(el, query),
  '/scheduling': async (el) => (await import('../screens/scheduling.js')).mountScheduling(el),
  '/approvals': async (el) => (await import('../screens/approvals.js')).mountApprovals(el),
  '/approval-routing': async (el, query) => (await import('../screens/approval_routing.js')).mountApprovalRouting(el, query),
  '/tasks': async (el, query) => (await import('../screens/tasks.js')).mountTasks(el, query),
  '/settings': async (el) => (await import('../screens/settings.js')).mountSettings(el),
  // Self-service Setup hub (2026-07-24) — consolidates the scattered config
  // screens (Settings/Branding/Tenant Config/Policy Hub/Knowledge Base/
  // Employees) behind one checklist, backed by the new getSetupStatus CF.
  // See screens/setup.js header for the full contract.
  '/setup': async (el) => (await import('../screens/setup.js')).mountSetup(el),
  '/org-access': async (el) => (await import('../screens/org_access.js')).mountOrgAccess(el),
  '/branches': async (el) => (await import('../screens/branches.js')).mountBranches(el),
  '/org-structure': async (el) => (await import('../screens/org_structure.js')).mountOrgStructure(el),
  '/position-profiles': async (el) => (await import('../screens/position_profiles.js')).mountPositionProfiles(el),
  '/public-holidays': async (el, query) => (await import('../screens/public_holidays.js')).mountPublicHolidays(el, query),
  '/vendors': async (el, query) => (await import('../screens/vendors.js')).mountVendors(el, query),
  '/purchase-orders': async (el, query) => (await import('../screens/purchase_orders.js')).mountPurchaseOrders(el, query),
  '/sales-orders': async (el, query) => (await import('../screens/sales_orders.js')).mountSalesOrders(el, query),
  // Stock dead-letter recon (2026-08-07 nb-08; GRADUATED 2026-08-12 FQ-12):
  // was a typed-hash-only route — now the full report surface over
  // getStockReconReports (filters + resolved history), carried in the rail
  // via pillars.js (Money pillar, ROLE_ADMIN_FINANCE + entitlement 'sales').
  // The CF re-gates admin|finance + 'sales' entitlement server-side.
  '/stock-recon': async (el) => (await import('../screens/stock_recon.js')).mountStockRecon(el),
  '/inventory': async (el) => (await import('../screens/inventory.js')).mountInventory(el),
  '/audit-log': async (el) => (await import('../screens/audit_log.js')).mountAuditLog(el),
  '/wps-gosi': async (el) => (await import('../screens/wps_gosi.js')).mountWpsGosi(el),
  // Filing Recovery — saga coordinator operator console (2026-08-19,
  // callable-has-caller gate closure). ROLE_ADMIN_ONLY in pillars.js;
  // getSagaOps re-enforces admin server-side regardless of this route.
  '/saga-ops': async (el) => (await import('../screens/saga_ops.js')).mountSagaOps(el),
  '/customers': async (el, query) => (await import('../screens/customers.js')).mountCustomers(el, query),
  '/nitaqat': async (el) => (await import('../screens/nitaqat.js')).mountNitaqat(el),
  '/recruitment': async (el) => (await import('../screens/recruitment.js')).mountRecruitment(el),
  '/documents': async (el) => (await import('../screens/documents.js')).mountDocuments(el),
  '/delegation': async (el) => (await import('../screens/delegation.js')).mountDelegation(el),
  '/expense-claims': async (el) => (await import('../screens/expense_claims.js')).mountExpenseClaims(el),
  '/work-requests': async (el, query) => (await import('../screens/work_requests.js')).mountWorkRequests(el, query),
  '/hr-letters': async (el) => (await import('../screens/hr_letters.js')).mountHrLetters(el),
  '/contracts': async (el, query) => (await import('../screens/contracts.js')).mountContracts(el, query),
  '/hr-requests-admin': async (el) => (await import('../screens/hr_requests_admin.js')).mountHrRequestsAdmin(el),
  '/request-type-builder': async (el) => (await import('../screens/request_type_builder.js')).mountRequestTypeBuilder(el),
  '/new-request': async (el) => (await import('../screens/request_submit.js')).mountRequestSubmit(el),
  '/grievances-admin': async (el) => (await import('../screens/grievances_admin.js')).mountGrievancesAdmin(el),
  '/org-chart': async (el) => (await import('../screens/org_chart.js')).mountOrgChart(el),
  '/tenant-config': async (el) => (await import('../screens/tenant_config.js')).mountTenantConfig(el),
  '/config-governance': async (el) => (await import('../screens/config_governance.js')).mountConfigGovernance(el),
  '/ai': async (el) => (await import('../screens/ai.js')).mountAi(el),
  '/insights': async (el) => (await import('../screens/insights.js')).mountInsights(el),
  '/financial-statements': async (el) => (await import('../screens/financial_statements.js')).mountFinancialStatements(el),
  '/gl-budgets': async (el) => (await import('../screens/gl_budgets.js')).mountGlBudgets(el),
  '/commissions': async (el) => (await import('../screens/commissions.js')).mountCommissions(el),
  '/my-work': async (el) => (await import('../screens/my_work.js')).mountMyWork(el),
  '/announcements': async (el, query) => (await import('../screens/announcements.js')).mountAnnouncements(el, query),
  '/incidents': async (el, query) => (await import('../screens/incidents.js')).mountIncidents(el, query),
  '/performance': async (el) => (await import('../screens/performance.js')).mountPerformance(el),
  '/succession': async (el) => (await import('../screens/succession.js')).mountSuccession(el),
  '/knowledge-base': async (el, query) => (await import('../screens/knowledge_base.js')).mountKnowledgeBase(el, query),
  '/team': async (el) => (await import('../screens/team.js')).mountTeam(el),
  '/reports': async (el) => (await import('../screens/reports.js')).mountReports(el),
  '/notifications': async (el) => (await import('../screens/notifications.js')).mountNotifications(el),
  '/gosi-filing': async (el) => (await import('../screens/gosi_filing.js')).mountGosiFiling(el),
  '/gosi-reconciliation': async (el) => (await import('../screens/gosi_reconciliation.js')).mountGosiReconciliation(el),
  '/pulse': async (el) => (await import('../screens/pulse.js')).mountPulse(el),
  '/feedback': async (el) => (await import('../screens/feedback.js')).mountFeedback(el),
  '/team-assignment-proposals': async (el) => (await import('../screens/team_assignment_proposals.js')).mountTeamAssignmentProposals(el),
  '/analytics': async (el) => (await import('../screens/analytics.js')).mountAnalytics(el),
  '/executive-telemetry': async (el) => (await import('../screens/executive_telemetry.js')).mountExecutiveTelemetry(el),
  '/workforce-intelligence': async (el) => (await import('../screens/workforce_intelligence.js')).mountWorkforceIntelligence(el),
  // ── Wave 5 verticals (read-only v1) ──────────────────────────────────
  '/projects': async (el) => (await import('../screens/projects.js')).mountProjects(el),
  '/rfis': async (el) => (await import('../screens/rfis.js')).mountRfis(el),
  '/change-orders': async (el) => (await import('../screens/change_orders.js')).mountChangeOrders(el),
  '/progress-claims': async (el) => (await import('../screens/progress_claims.js')).mountProgressClaims(el),
  '/subcontractors': async (el) => (await import('../screens/subcontractors.js')).mountSubcontractors(el),
  '/hospitality-reservations': async (el) => (await import('../screens/hospitality_reservations.js')).mountHospitalityReservations(el),
  '/hospitality-folio': async (el) => (await import('../screens/hospitality_folio.js')).mountHospitalityFolio(el),
  '/hospitality-housekeeping': async (el) => (await import('../screens/hospitality_housekeeping.js')).mountHospitalityHousekeeping(el),
  '/manufacturing-production': async (el) => (await import('../screens/manufacturing_production.js')).mountManufacturingProduction(el),
  '/manufacturing-materials': async (el) => (await import('../screens/manufacturing_materials.js')).mountManufacturingMaterials(el),
  '/manufacturing-quality': async (el) => (await import('../screens/manufacturing_quality.js')).mountManufacturingQuality(el),
  // ── Integration wave (2026-07-15) — tickets/workstreams/journeys/crm ──
  '/tickets': async (el, query) => (await import('../screens/tickets.js')).mountTickets(el, query),
  '/workstreams': async (el, query) => (await import('../screens/workstreams.js')).mountWorkstreams(el, query),
  '/issue-ledger': async (el) => (await import('../screens/issue_ledger.js')).mountIssueLedger(el),
  '/issue-threads': async (el, query) => (await import('../screens/issue_threads.js')).mountIssueThreads(el, query),
  '/journeys': async (el, query) => (await import('../screens/journeys.js')).mountJourneys(el, query),
  '/crm': async (el, query) => (await import('../screens/crm.js')).mountCrm(el, query),
  // ── Shared-file integrator pass (2026-07-15) — manufacturing work-orders +
  // maintenance/FM ────────────────────────────────────────────────────────
  '/manufacturing-work-orders': async (el, query) => (await import('../screens/manufacturing_work_orders.js')).mountManufacturingWorkOrders(el, query),
  '/maintenance': async (el) => (await import('../screens/maintenance.js')).mountMaintenance(el),
  // ── Shared-file integrator pass (2026-07-15, continued) — fixed assets ──
  '/fixed-assets': async (el) => (await import('../screens/fixed_assets.js')).mountFixedAssets(el),
  // ── Shared-file integrator pass (2026-07-15, continued) — AR + AP cockpits ──
  '/ar-cockpit': async (el) => (await import('../screens/ar_cockpit.js')).mountArCockpit(el),
  '/ap-cockpit': async (el, query) => (await import('../screens/ap_cockpit.js')).mountApCockpit(el, query),
  // ── Shared-file integrator pass (2026-07-16, continued) — Muqeem / Iqama
  // Expiry board ──────────────────────────────────────────────────────────
  '/muqeem-iqama': async (el) => (await import('../screens/muqeem_iqama.js')).mountMuqeemIqama(el),
  // ── Shared-file integrator pass (2026-07-16, continued) — Offboarding /
  // Terminations board ─────────────────────────────────────────────────────
  '/offboarding': async (el, query) => (await import('../screens/offboarding.js')).mountOffboarding(el, query),
  // ── Wave-1 integration pass (2026-07-16) — messages / recurring-payroll /
  // attendance-config / offer-letters (leave_exemptions stayed unregistered
  // in THIS pass as a no-backend-yet stub; it gained its CF pair + its
  // '/leave-exemptions' entry below in the 2026-08 waves) ──────────────────
  '/messages': async (el) => (await import('../screens/messages.js')).mountMessages(el),
  '/recurring-payroll': async (el) => (await import('../screens/recurring_payroll.js')).mountRecurringPayroll(el),
  '/attendance-config': async (el) => (await import('../screens/attendance_config.js')).mountAttendanceConfig(el),
  '/offer-letters': async (el) => (await import('../screens/offer_letters.js')).mountOfferLetters(el),
  // ── Wave-2 integration pass (2026-07-16) — finance ledgers + hospitality
  // depth (trial-balance/cash-ledger/hospitality rooms-deposits-tax/leases) ──
  '/trial-balance': async (el, query) => (await import('../screens/trial_balance.js')).mountTrialBalance(el, query),
  '/cash-ledger': async (el, query) => (await import('../screens/cash_ledger.js')).mountCashLedger(el, query),
  '/hospitality-rooms': async (el, query) => (await import('../screens/hospitality_rooms.js')).mountHospitalityRooms(el, query),
  '/hospitality-deposits': async (el, query) => (await import('../screens/hospitality_deposits.js')).mountHospitalityDeposits(el, query),
  '/hospitality-tax': async (el, query) => (await import('../screens/hospitality_tax.js')).mountHospitalityTax(el, query),
  '/leases': async (el, query) => (await import('../screens/leases.js')).mountLeases(el, query),
  // ── Launch-critical + fast-follow integrator pass (2026-07-17) — privacy/
  // billing/policy-hub/recycle-bin/calendar-export/id-cards/request-type
  // config ─────────────────────────────────────────────────────────────────
  '/privacy': async (el) => (await import('../screens/privacy.js')).mountPrivacy(el),
  '/billing': async (el) => (await import('../screens/billing.js')).mountBilling(el),
  '/policy-hub': async (el) => (await import('../screens/policy_hub.js')).mountPolicyHub(el),
  '/recycle-bin': async (el) => (await import('../screens/recycle_bin.js')).mountRecycleBin(el),
  '/calendar-export': async (el) => (await import('../screens/calendar_export.js')).mountCalendarExport(el),
  '/id-cards': async (el) => (await import('../screens/id_cards.js')).mountIdCards(el),
  '/request-type-config': async (el) => (await import('../screens/request_type_config.js')).mountRequestTypeConfig(el),
  // ── K8 config-not-code — Pack Calculated Fields (read-first calc-field
  // display + non-money pack_config value editor over listAvailablePacks +
  // the existing writeTenantConfig ConfigPlane path; zero new CFs/rules) ──
  '/pack-calc-config': async (el) => (await import('../screens/pack_calc_config.js')).mountPackCalcConfig(el),
  // Both added by the 2026-08-07 FQ wave (62541e5d9), which registered the
  // pillars.js nav items but NOT these mounters — so each nav entry silently
  // rendered mountPlaceholder instead of its screen (the `mount || placeholder`
  // fallback at the top of this file swallows a missing mounter). Caught by
  // scripts/gate-checks/webadmin-reachability.mjs on its first run, 2026-08-08.
  // Manufacturing planning — BOMs / work centers / routings. Registered by the
  // orchestrator (shell.js is shared; the builder returned this line instead of
  // editing it, so 4 concurrent lanes could not race this file).
  '/manufacturing-planning': async (el, query) => (await import('../screens/manufacturing_planning.js')).mountManufacturingPlanning(el, query),
  '/doc-numbering': async (el) => (await import('../screens/doc_numbering.js')).mountDocNumbering(el),
  '/leave-exemptions': async (el) => (await import('../screens/leave_exemptions.js')).mountLeaveExemptions(el),
  // GL Period Close — deliberate close console over the EXISTING
  // financeClosePeriod callable (admin|finance, Firestore-backed role gate;
  // see screens/period_close.js header for the full CF contract). Wired
  // 2026-08-12 (portal had no period-close surface at all; the Flutter app
  // was the only close UI).
  '/period-close': async (el) => (await import('../screens/period_close.js')).mountPeriodClose(el),
  // FX Rate Table (2026-08-12, reachability sweep): first consumer of
  // listTenantFxRates + the writeTenantConfig 'glFxRates' domain. Feeds the
  // revaluation Period Close depends on (FX_REVALUATION_REQUIRED). The CFs
  // re-gate admin|hr (Firestore-resolved, fail-closed) server-side.
  '/fx-rates': async (el) => (await import('../screens/fx_rates.js')).mountFxRates(el),
  // GL Postings & Reversals — first operator surface for financeReversePosting
  // (PACKET_unreachable_callables_2026-08-19.md cluster 4, dual control by
  // default). See screens/gl_postings.js header for the full CF contract.
  '/gl-postings': async (el) => (await import('../screens/gl_postings.js')).mountGlPostings(el),
  // Revenue Engine — read-only console over the EXISTING getRevenue callable
  // (its first consumer anywhere in the portal; see screens/revenue.js header
  // for the full CF contract). Wired 2026-08-12.
  '/revenue': async (el) => (await import('../screens/revenue.js')).mountRevenue(el),
  // PDPL Breach Register — first consumer of breachMutate (PDPL Art.24
  // register + 72h SDAIA deadline engine; reachability sweep 2026-08-12).
  // Nav gate admin-only: the CF's {admin, dpo} set is effectively admin —
  // 'dpo' is unassignable (see screens/breach_register.js header).
  '/breach-register': async (el) => (await import('../screens/breach_register.js')).mountBreachRegister(el),
  // Dunning Configuration — form over setDunningConfig + the new
  // getDunningConfig read half (the write is OCC-versioned; the read returns
  // the version, unblinding the form — see screens/dunning_config.js header).
  '/dunning-config': async (el) => (await import('../screens/dunning_config.js')).mountDunningConfig(el),
  // Shift open/close Checklists — first web consumer of checklistMutate
  // (WEB_PARITY #27 residual; the CF had ZERO web refs before 2026-08-14).
  // Templates admin + daily runner over the 6 existing ops; reads are direct
  // Firestore (no read CF exists) under the already-landed server-only rules
  // blocks. See screens/checklists.js header for the full CF contract.
  '/checklists': async (el) => (await import('../screens/checklists.js')).mountChecklists(el),
  // Integrations — Outbound Webhooks + Connected Services status board
  // (2026-08-25, .scratch/INTEGRATIONS_CATALOG_2026-08-25.md §4 item #1).
  // First tenant-facing integrations surface in web-admin — distinct from
  // the hidden platform-ops '/platform-integrations' route above. See
  // screens/integrations.js header for the full CF + read contract.
  '/integrations': async (el) => (await import('../screens/integrations.js')).mountIntegrations(el),
};

/**
 * mountDegradedSessionBanner — the manual escape hatch shown ONLY when
 * mountShellFrame was reached via a bootstrap that genuinely failed (timed
 * out / no data) even after the bounded automatic retry in
 * attemptBootstrapAndMount. A full page reload is the simplest reliable
 * retry here: it re-runs main.js's onAuthStateChanged flow from scratch
 * (fresh getBootstrap, fresh route registration) rather than re-invoking
 * mountShellFrame in place, which would double-register the router's
 * 'hashchange' listener (lib/router.js's startRouter has no idempotency
 * guard) on every manual retry.
 */
export function mountDegradedSessionBanner(root) {
  const shellEl = root.querySelector('#app-shell');
  if (!shellEl) return;
  const banner = document.createElement('div');
  banner.setAttribute('role', 'status');
  // flex:0 0 auto pins the banner to its content height inside #app-shell's
  // column flex layout (tokens.css: #app-shell {display:flex;flex-
  // direction:column;height:100vh}) so it takes its own space at the top
  // instead of being squashed/overflowing against .shell-body's height:100%.
  banner.style.cssText = 'flex:0 0 auto;display:flex;align-items:center;gap:10px;padding:9px 16px;font-size:12.5px;color:var(--text);background:var(--bg-ch);border-bottom:1px solid var(--border-r)';
  banner.innerHTML = `
    <span>${escapeHtmlShell(t('shell.degradedAccess'))}</span>
    <button type="button" id="shell-degraded-retry" class="btn btn-ghost btn-sm" style="padding:3px 10px">${escapeHtmlShell(t('common.retry'))}</button>`;
  shellEl.prepend(banner);
  banner.querySelector('#shell-degraded-retry')?.addEventListener('click', () => {
    window.location.reload();
  });
}

/**
 * mountImpersonationBanner — persistent, unmissable banner naming the tenant
 * being viewed and the expiry, shown for as long as the caller's ID token
 * carries an active `impersonation` claim (set by adminMintImpersonationGrant,
 * functions/src/platform/auth/impersonation_service.ts; cleared on
 * revoke/expiry). Design requirement from cluster 1 of
 * .scratch/PACKET_unreachable_callables_2026-08-19.md: "An operator who
 * forgets they are impersonating is how audit trails become useless."
 *
 * Read-only check of the token — no new CF, no rules edit. `exp` is the
 * SAME belt-and-braces in-claim expiry impersonation_service.ts's own
 * assertNotReadOnlyImpersonation relies on; a past-exp claim (stale, not yet
 * refreshed away) is treated as inactive here too rather than shown as live.
 * Idempotent (checks for an existing banner id) so re-mounts (language
 * toggle's reRenderActiveRoute, etc.) never stack duplicate banners.
 *
 * "Stop impersonating" calls the SAME adminRevokeImpersonationGrant the
 * platform-auth console's own Revoke button calls, then forces a token
 * refresh (mirrors this module's own doc-comment instruction in
 * impersonation_service.ts: revoke/expiry requires getIdToken(true) to drop
 * the claim) and reloads so every screen's state is consistent with the
 * now-cleared claim.
 */
export function mountImpersonationBanner(root, user) {
  const shellEl = root.querySelector('#app-shell');
  if (!shellEl || !user) return;
  user.getIdTokenResult().then((res) => {
    if (!shellEl.isConnected) return;
    const claims = (res && res.claims) || {};
    const imp = claims.impersonation;
    if (!imp || !imp.impersonating) return;
    const expMs = Number(imp.exp) || 0;
    if (expMs && expMs <= Date.now()) return; // stale in-claim expiry — never show a dead banner
    if (shellEl.querySelector('#shell-impersonation-banner')) return; // already mounted

    const banner = document.createElement('div');
    banner.id = 'shell-impersonation-banner';
    banner.setAttribute('role', 'status');
    // Deliberately NOT var(--bg-ch) like the degraded banner — impersonation
    // must read as visually distinct/urgent, not blend in as routine chrome.
    banner.style.cssText = 'flex:0 0 auto;display:flex;align-items:center;gap:10px;padding:9px 16px;font-size:12.5px;font-weight:600;color:#3a2600;background:#ffc63d;border-bottom:1px solid var(--border-r)';
    const modeLabel = imp.impersonationReadOnly === false ? t('shell.impersonationWrite') : t('shell.impersonationReadOnly');
    const expiryLabel = expMs ? new Date(expMs).toLocaleString() : '—';
    banner.innerHTML = `
      <span aria-hidden="true">⚠</span>
      <span>${escapeHtmlShell(t('shell.impersonationBannerText', { tenant: String(imp.impersonating), mode: modeLabel, expiry: expiryLabel }))}</span>
      <button type="button" id="shell-impersonation-stop" class="btn btn-ghost btn-sm" style="padding:3px 10px;margin-inline-start:auto;color:#3a2600;border-color:#3a2600">${escapeHtmlShell(t('shell.impersonationStop'))}</button>`;
    shellEl.prepend(banner);

    banner.querySelector('#shell-impersonation-stop')?.addEventListener('click', async () => {
      const btn = banner.querySelector('#shell-impersonation-stop');
      if (btn) btn.disabled = true;
      try {
        await callFn('adminRevokeImpersonationGrant', { grantId: imp.grantId });
      } catch (err) {
        console.error('[shell] adminRevokeImpersonationGrant failed', err);
      }
      try {
        await user.getIdToken(true); // force refresh so the claim actually drops
      } catch {
        // swallow — the reload below re-runs the whole auth flow regardless
      }
      window.location.reload();
    });
  }).catch(() => {
    // fail-closed on the BANNER only (never show a false-positive banner) —
    // this is a display concern; the real read-only enforcement lives
    // server-side (assertNotReadOnlyImpersonation) + in firestore.rules and
    // is unaffected by this catch.
  });
}

export function populateUserChip(root, user) {
  const nameEl = root.querySelector('#sidebar-name');
  const roleEl = root.querySelector('#sidebar-role');
  const badgeEl = root.querySelector('#header-role-badge');
  const avEl = root.querySelector('#sidebar-av');
  const headerAvEl = root.querySelector('#header-avatar');
  const menuNameEl = root.querySelector('#account-menu-name');
  const menuEmailEl = root.querySelector('#account-menu-email');

  // Fallback from the Firebase Auth user record while bootstrap resolves.
  const fallbackName = user.email || 'User';
  nameEl.textContent = fallbackName;
  roleEl.textContent = '—';
  badgeEl.textContent = '…';
  avEl.textContent = initialsFrom(fallbackName);
  headerAvEl.textContent = initialsFrom(fallbackName);
  if (menuNameEl) menuNameEl.textContent = fallbackName;
  if (menuEmailEl) menuEmailEl.textContent = user.email || '';

  getBootstrap().then((result) => {
    // Update connection state separately (callables-alive = normal, timeouts = limited, auth-errors = offline)
    updateConnectionStatus(result);

    const { data } = result;
    const profile = data && data.profileExists ? data.profile : null;
    const displayName = (profile && profile.name) || fallbackName;
    const role = (profile && profile.role) || '—';
    // roleLabel (pillar_home.js) resolves the role token through t() —
    // 2026-07-15 i18n fix; the raw token used to render verbatim here in
    // both languages (mirrors the pillar_home.js springboard role-chip bug).
    const displayRole = roleLabel(role);
    nameEl.textContent = displayName;
    roleEl.textContent = displayRole;

    // Badge now shows connection state, not 'offline' as a role fallback
    const connectionBadge = getConnectionBadgeText();
    badgeEl.textContent = connectionBadge || displayRole.toUpperCase();
    const initials = initialsFrom(displayName);
    avEl.textContent = initials;
    headerAvEl.textContent = initials;
    if (menuNameEl) menuNameEl.textContent = displayName;
  });
}

/**
 * Loads the tenant's compact branding logo (companies/{tid}/branding/compact
 * — see lib/domain/services/tenant_branding_service.dart / tenant_logo.dart
 * for the ground-truth schema) and swaps it in for the STAFFLY wordmark in
 * the sidebar header. Fallback chain: tenant logo → STAFFLY wordmark (never
 * a broken image, never a spinner — on timeout/error/missing doc the
 * STAFFLY fallback that's already on screen just stays).
 */
export function populateTenantLogo(root) {
  const textEl = root.querySelector('#sidebar-logo-text');
  const imgEl = root.querySelector('#sidebar-logo-img');
  const nameEl = root.querySelector('#sidebar-company-name');
  if (!textEl || !imgEl) return;

  getBootstrap().then(({ data, timedOut, error }) => {
    const tid = data && data.companyId;
    if (timedOut || error || !tid) return; // STAFFLY fallback stays.

    // Company NAME as text under the logo — so a logo-less tenant (STAFFLY
    // wordmark) is still identifiable, and a logo'd tenant is named too.
    // Guarded + fail-silent: on any failure the logo/wordmark simply stays
    // with no name label (never an error, never the raw tenant id).
    // Construct each Firestore read INSIDE a try so this branch is actually
    // "fail-silent on any failure" as documented above: `getDoc(doc(db, …))` is
    // evaluated as an argument BEFORE guardedRead runs, so a synchronous throw
    // from ref construction would escape guardedRead entirely and surface as an
    // uncaught pageerror on EVERY screen (the shell mounts once per session) —
    // not the intended silent fallback. On any such throw the logo/wordmark
    // simply stays with no name/image, never an error.
    if (nameEl) {
      let nameReadPromise = null;
      try {
        nameReadPromise = getDoc(doc(db, 'companies', tid));
      } catch (_refErr) { nameReadPromise = null; }
      if (nameReadPromise) {
        guardedRead(nameReadPromise, { timeoutMs: 9000, label: 'tenant-name' }).then(
          ({ data: snap, timedOut: nameTimedOut, error: nameError }) => {
            if (nameTimedOut || nameError || !snap || !snap.exists()) return;
            const c = snap.data() || {};
            const name = c.name || c.companyName || c.tenantName;
            if (!name) return;
            nameEl.textContent = String(name);
            nameEl.hidden = false;
          },
        );
      }
    }

    let logoReadPromise = null;
    try {
      logoReadPromise = getDoc(doc(db, 'companies', tid, 'branding', LOGO_VARIANT_KEY));
    } catch (_refErr) { logoReadPromise = null; }
    if (logoReadPromise) {
      guardedRead(logoReadPromise, { timeoutMs: 9000, label: 'tenant-logo' }).then(
        ({ data: snap, timedOut: logoTimedOut, error: logoError }) => {
          if (logoTimedOut || logoError || !snap || !snap.exists()) return;
          const url = snap.data().url;
          if (!url) return; // doc exists but no image uploaded — STAFFLY fallback stays.
          imgEl.src = url;
          imgEl.style.display = '';
          textEl.style.display = 'none';
        },
      );
    }
  });
}

function initialsFrom(name) {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function closeMobileSidebar(root) {
  root.querySelector('.sidebar').classList.remove('open');
  root.querySelector('#sidebar-overlay').classList.remove('visible');
}

/** Opens the command palette from the top-bar search box (click or keyboard
 * focus). The palette owns its own input + Cmd/Ctrl+K listener; this is a
 * discoverability affordance only, so a double-invoke (click + focus) is safe
 * (openCommandPalette no-ops when the palette is already open). */
function wireHeaderSearch(root) {
  const box = root.querySelector('#header-search');
  if (!box) return;
  const open = (e) => { if (e) e.preventDefault(); openCommandPalette(); };
  box.addEventListener('click', open);
  box.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') open(e);
  });
  root.querySelector('#header-search-input')?.addEventListener('focus', () => openCommandPalette());
}

/**
 * wireSuperadminEntry — hidden Platform Ops entry point (2026-07-21). A
 * double-click on the sidebar brand logo (`.sidebar-logo` — wraps both the
 * default STAFFLY wordmark `#sidebar-logo-text` and any swapped-in tenant
 * logo `#sidebar-logo-img`, see shellTemplate()/populateTenantLogo()) checks
 * the CURRENT caller's ID token for the platform-tier claim via
 * isPlatformAccount (the EXACT same predicate the platformAccountTemplate
 * interstitial above and server-side requirePlatformRole use) and, only if
 * positive, navigates to the internal Superadmin/Platform-Ops screen
 * (#/platform, screens/superadmin.js) — the replacement for the
 * never-shipped `zephyr-ops-7q3k` Flutter Superadmin Portal.
 *
 * For every other (tenant-only) caller — the overwhelming majority of
 * admin/hr/manager/finance sessions that reach mountShellFrame — the
 * double-click is a SILENT no-op: no toast, no error, no visible change.
 * This is a deliberately hidden entry, not a discoverable affordance, so a
 * regular tenant user has zero indication it exists. Reused across a
 * sign-out/sign-in cycle safely: the logo element itself is torn down and
 * rebuilt by shellTemplate() on every mountShellFrame() call (root.innerHTML
 * replace), so the old listener is discarded with it — no AbortController
 * bookkeeping needed here (unlike wireAccountMenu's document-level
 * listeners, which outlive a single shellTemplate() render).
 */
function wireSuperadminEntry(root, user) {
  const logoEl = root.querySelector('.sidebar-logo');
  if (!logoEl) return;
  logoEl.addEventListener('dblclick', () => {
    isPlatformAccount(user).then((isPlatform) => {
      if (!isPlatform) return; // silent no-op — no affordance, no feedback
      navigate('/platform');
    });
  });
}

/** Populates the notification bell's count badge from getNotifications
 * (the same self-scoped read the Notifications screen uses). Non-blocking +
 * fail-silent: a timeout/denial/error just leaves the badge hidden. */
export function loadNotifCount(root) {
  const badge = root.querySelector('#header-notif-count');
  if (!badge) return;
  guardedRead(callFn('getNotifications'), { timeoutMs: 12000, label: 'notif-count' }).then(({ data }) => {
    const unread = data && data.ok && data.counts ? Number(data.counts.unread) || 0 : 0;
    if (unread <= 0) return; // count 0 → render nothing
    badge.textContent = unread > 99 ? '99+' : String(unread);
    badge.hidden = false;
  });
}

/** Records a real-screen visit into the per-pillar Recents list (localStorage,
 * client-only — see pillar_home.js). Springboard/pillar routes and unknown
 * paths are ignored; only ALL_SCREENS entries count. Only the ROUTE is
 * persisted (2026-07-15 i18n fix) — pillar_home.js's chipDisplayLabel
 * resolves the display label from ALL_SCREENS via labelKey at render time,
 * so freezing today's raw EN screen.label into localStorage would leave a
 * stale English chip behind after a language flip. */
function recordScreenVisit(path) {
  const screen = ALL_SCREENS.find((i) => i.path === path);
  if (!screen) return;
  const owning = pillarOwning(path);
  if (!owning) return;
  recordRecentVisit(owning.id, path);
}

/**
 * updateActiveNav — the ONE handler wired to onRouteChange: highlights the
 * rail item for the pillar owning `path` (whether `path` IS a pillar's own
 * springboard route or one of its screens — pillarOwning in pillars.js
 * resolves either), and sets the header title/breadcrumb. Gold only applies
 * to the active Compliance rail item (never at rest — see tokens.css
 * `.labeled-rail-item.active.gold`).
 */
function updateActiveNav(root, path) {
  const owningPillar = pillarOwning(path);
  root.querySelectorAll('.labeled-rail-item').forEach((el) => {
    const isActive = !!owningPillar && el.dataset.pillar === owningPillar.id;
    el.classList.toggle('active', isActive);
    el.classList.toggle('gold', isActive && !!owningPillar.gold);
  });

  const screenItem = ALL_SCREENS.find((i) => i.path === path);
  const titleEl = root.querySelector('#page-title');
  const breadcrumbEl = root.querySelector('#page-breadcrumb');
  if (titleEl) {
    const title = screenItem ? screenLabel(screenItem) : owningPillar ? pillarLabel(owningPillar) : 'Staffly';
    titleEl.childNodes[0].nodeValue = `${title} `;
  }
  if (breadcrumbEl) {
    breadcrumbEl.textContent = screenItem && owningPillar ? `/ ${pillarLabel(owningPillar)}` : '';
  }
}

/** Maps each of the 5 pillars.js pillar ids to its i18n/en.js + ar.js
 * `nav.*` key (see en.js "nav (pillars.js sidebar groups)" section) — a
 * shell-local lookup rather than a pillars.js edit, so pillars.js stays the
 * single, structurally-unchanged source of truth for routes (per this
 * change's scope) while the 5 pillar names the shell itself renders (rail,
 * command palette, page title/breadcrumb) go through t() and flip with the
 * language toggle. Screen-level labels (30+ of them) have no translation
 * keys yet and are deliberately left as-is — out of scope here. */
const NAV_LABEL_KEYS = {
  people: 'nav.people',
  money: 'nav.money',
  operations: 'nav.operations',
  compliance: 'nav.compliance',
  insights: 'nav.insightsTools',
  verticals: 'nav.verticals',
};

/** Resolves a pillar's display label through t() when a nav.* key exists
 * for it, else falls back to the pillar's own hardcoded label. */
function pillarLabel(pillar) {
  const key = NAV_LABEL_KEYS[pillar.id];
  return key ? t(key) : pillar.label;
}

/** Resolves a screen entry's display label through t() at call time
 * (pillars.js screens[] now carry labelKey — 2026-07-15 i18n tile-label
 * fix), falling back to the raw EN label for any entry without one. Used
 * by the page title/breadcrumb and the command-palette items so they flip
 * with the language toggle like the rail does. */
function screenLabel(screen) {
  return screen.labelKey ? t(screen.labelKey) : screen.label;
}

/** The command palette's {label, items} groups, with every screen label
 * resolved through t() AT BUILD TIME — rebuilt on every language change by
 * wireLangReactivity's mountCommandPalette call, so palette entries flip
 * language like the rail (they previously leaked raw EN labels in AR mode). */
function paletteSections(pillars) {
  return pillars.map((p) => ({
    label: pillarLabel(p),
    items: p.screens.map((s) => ({ ...s, label: screenLabel(s) })),
  }));
}

function renderRailItem(pillar) {
  return `
    <button type="button" class="labeled-rail-item" data-pillar="${pillar.id}">
      <span class="li-icon">${pillar.icon}</span>
      <span>${pillarLabel(pillar)}</span>
    </button>`;
}

/** Builds the rail's nav buttons from `pillars` and wires their click
 * handlers. Factored out of mountShellFrame so the language-change
 * listener (wireLangReactivity) can call it again to refresh pillar labels
 * without duplicating this markup/wiring logic — each call fully replaces
 * #sidebar-nav's innerHTML, so the old DOM nodes (and whatever listeners
 * were bound to them) are discarded together rather than accumulating. */
function renderSidebarNav(root, pillars) {
  const sidebarNavEl = root.querySelector('#sidebar-nav');
  if (!sidebarNavEl) return;
  sidebarNavEl.innerHTML = pillars.map(renderRailItem).join('');

  // Rail clicks navigate to that pillar's springboard via the hash router
  // (real history entries → real back button).
  sidebarNavEl.querySelectorAll('[data-pillar]').forEach((el) => {
    el.addEventListener('click', () => {
      navigate(`/pillar/${el.dataset.pillar}`);
      closeMobileSidebar(root);
    });
  });
}

// ── Account menu (avatar dropdown) ──────────────────────────────────────

// AbortController lets a re-mount (sign-out → sign back in) tear down the
// previous mount's document-level listeners instead of stacking a second
// set on every mountShell() call.
let _accountMenuAbort = null;

function wireAccountMenu(root) {
  const avatarBtn = root.querySelector('#header-avatar');
  const menuEl = root.querySelector('#account-menu');
  const dropdownEl = root.querySelector('#account-dropdown');
  if (!avatarBtn || !menuEl || !dropdownEl) return;

  if (_accountMenuAbort) _accountMenuAbort.abort();
  _accountMenuAbort = new AbortController();
  const { signal } = _accountMenuAbort;

  const closeMenu = () => dropdownEl.classList.remove('open');

  avatarBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    dropdownEl.classList.toggle('open');
  });
  document.addEventListener('click', (e) => {
    if (!menuEl.contains(e.target)) closeMenu();
  }, { signal });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeMenu();
  }, { signal });

  root.querySelector('#account-menu-profile')?.addEventListener('click', () => {
    closeMenu();
    navigate('/settings');
  });
  root.querySelector('#account-menu-signout')?.addEventListener('click', () => {
    closeMenu();
    signOut(auth);
  });
}

// ── Language toggle (EN | ع) ─────────────────────────────────────────────
// Wires the shell's pre-existing #lang-toggle button to the shared i18n
// store (src/i18n/store.js) — setLang()/getLang() — instead of a
// shell-local direction flag, so the ONE control drives both translated
// copy (t(), see screens/feedback.js, the i18n reference screen) and
// interface direction (getDir()/rtl.css). setLang() persists to
// localStorage ('staffly.lang'), stamps <html lang/dir> itself, and fires
// the 'staffly:langchange' notification that wireLangReactivity (below)
// subscribes to for the live re-render.

/** Updates the toggle button's label/title from the CURRENT language —
 * pure sync, no click binding, so it's safe to call repeatedly (e.g. from
 * the langchange listener) without stacking event handlers. */
export function syncLangToggle(root) {
  const btn = root.querySelector('#lang-toggle');
  if (!btn) return;
  const isAr = getLang() === 'ar';
  btn.textContent = isAr ? 'EN' : 'ع';
  const label = isAr ? 'Switch to English' : 'التبديل إلى العربية';
  btn.title = label;
  btn.setAttribute('aria-label', label);
}

/** Binds the toggle's click handler ONCE (called only from the initial
 * mountShellFrame pass, unlike syncLangToggle which also runs on every
 * langchange). Clicking flips between 'en' and 'ar' via setLang(), which
 * itself triggers syncLangToggle + the full reactive re-render through the
 * langchange listener wired by wireLangReactivity — this handler does not
 * need to do that work itself. */
export function wireLangToggle(root) {
  const btn = root.querySelector('#lang-toggle');
  if (!btn) return;
  syncLangToggle(root);
  btn.addEventListener('click', () => {
    setLang(getLang() === 'ar' ? 'en' : 'ar');
  });
}

/** Re-runs the CURRENTLY active route's mount function against
 * #content-region — the same lookup-and-remount shape router.js's own
 * (private, unexported) render() uses, just reading from mountShellFrame's
 * local `routeTable` instead of router.js's internal `routes` Map (which
 * this file has no access to). This is intentionally NOT a new router: it
 * reuses the identical path→mount-fn entries that were just handed to
 * registerRoute for this exact mount, so navigating away and back afterward
 * still goes through the real router unchanged. */
function reRenderActiveRoute(root, routeTable) {
  const contentEl = root.querySelector('#content-region');
  if (!contentEl) return;
  const { path, query } = splitPath(getCurrentPath());
  const renderFn = routeTable.get(path) || routeTable.get('__notfound__');
  contentEl.innerHTML = '';
  if (renderFn) renderFn(contentEl, query);
}

// Unsubscribe handle for the CURRENT mountShellFrame's language listener —
// mirrors _accountMenuAbort above: a sign-out/sign-in cycle calls
// mountShellFrame again, and without this the previous mount's listener
// would stay registered on the shared i18n store forever, firing a
// re-render against a detached `root` on every future language change.
let _langListenerUnsub = null;

// Same contract for the router's route-change listener (see the LEAK FIX
// note at the onRouteChange call in mountShellFrame).
let _routeChangeUnsub = null;

/** Subscribes ONCE per mountShellFrame call to language changes, refreshing
 * everything this shell chrome renders that depends on the active
 * language/direction: the toggle button itself, the rail's pillar labels,
 * the command palette's group labels, the active-nav title/breadcrumb, and
 * — the acceptance bar — the currently mounted screen's own content (via
 * reRenderActiveRoute), so a screen built on t() (feedback.js) actually
 * flips language live instead of only on next navigation. */
function wireLangReactivity(root, user, pillars, routeTable) {
  if (_langListenerUnsub) _langListenerUnsub();
  _langListenerUnsub = addLangListener(() => {
    syncLangToggle(root);
    syncThemeToggle(root);
    syncHeaderSearchLang(root);
    renderSidebarNav(root, pillars);
    mountCommandPalette(paletteSections(pillars));
    updateActiveNav(root, splitPath(getCurrentPath()).path);
    reRenderActiveRoute(root, routeTable);
    // Re-resolves the sidebar/header role text through roleLabel() with the
    // NEW active language (2026-07-15 i18n fix) — populateUserChip only ran
    // once at mount otherwise, leaving the persistent sidebar role chip
    // stale after a live language flip even though it now translates
    // correctly at initial mount.
    populateUserChip(root, user);
  });
}

/** Refreshes the top-bar search box's placeholder/labels from the ACTIVE
 * language (the shellTemplate renders them once at mount with whatever
 * language was active then — without this, flipping to AR left the English
 * "Search" placeholder on screen, the CK-flagged live bug). */
function syncHeaderSearchLang(root) {
  const label = t('common.search');
  const box = root.querySelector('#header-search');
  if (box) {
    box.setAttribute('aria-label', label);
    box.title = `${label} (Ctrl/⌘ K)`;
  }
  const input = root.querySelector('#header-search-input');
  if (input) {
    input.placeholder = label;
    input.setAttribute('aria-label', label);
  }
}

// ── Light/dark theme toggle ─────────────────────────────────────────────
// Activates the rose-light theme (html[data-theme='light'] overrides in
// src/styles/tokens.css) which was fully wired but unreachable until this
// control existed. The FOUC-free initial value is set by the inline <head>
// script in index.html (reads the same localStorage key before first
// paint); this just keeps it in sync after mount and lets the user flip it.
// Login screen is intentionally dark-only (per the theming pass) and is
// never wired to this — the toggle only exists in the authenticated shell.

const THEME_STORAGE_KEY = 'staffly-theme';

export function currentTheme() {
  return document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark';
}

/** Resolves which pre-rendered real-logo PNG (web-admin/public/, copied from
 * assets/images/{dark,light}_logo.png) to show for the current data-theme —
 * dark_logo.png (light glyph + wordmark) reads on the app's default dark
 * surfaces, light_logo.png (dark charcoal glyph + wordmark) reads on the
 * rose-light theme. Exported so employee_shell.js's sidebar — which shares
 * this same theme system via the imported wireThemeToggle below — computes
 * the SAME src instead of forking the light/dark mapping. */
export function defaultLogoSrc() {
  return currentTheme() === 'light' ? '/light_logo.png' : '/dark_logo.png';
}

function setTheme(theme) {
  document.documentElement.setAttribute('data-theme', theme);
  try { localStorage.setItem(THEME_STORAGE_KEY, theme); } catch { /* best-effort */ }
}

/** Updates the theme toggle's icon/aria-label/title from the CURRENT theme —
 * pure sync, no click binding (mirrors syncLangToggle above), so
 * wireLangReactivity can call it again on a language change without
 * re-registering the click listener. Labels route through t() (2026-07-15
 * i18n fix — these used to be raw English regardless of active language). */
export function syncThemeToggle(root) {
  const btn = root.querySelector('#theme-toggle');
  if (!btn) return;
  const isLight = currentTheme() === 'light';
  btn.innerHTML = isLight ? ICON_SUN : ICON_MOON;
  const label = isLight ? t('common.switchToDarkTheme') : t('common.switchToLightTheme');
  btn.setAttribute('aria-label', label);
  btn.title = label;
  // Default (non-tenant) sidebar logo has a light-bg and dark-bg variant —
  // flip it in step with the theme so it stays legible (tenant logos,
  // swapped in by populateTenantLogo, live on a separate #sidebar-logo-img
  // element and are untouched by this).
  const logoEl = root.querySelector('#sidebar-logo-default');
  if (logoEl) logoEl.src = defaultLogoSrc();
}

export function wireThemeToggle(root) {
  const btn = root.querySelector('#theme-toggle');
  if (!btn) return;
  syncThemeToggle(root);
  btn.addEventListener('click', () => {
    setTheme(currentTheme() === 'light' ? 'dark' : 'light');
    syncThemeToggle(root);
  });
}

/** Sun / moon icons for the theme toggle (same 15x15 stroke language as
 * shell/pillars.js ICONS — kept local to shell.js since the toggle is a
 * shell-chrome control, not a nav/screen icon). The icon shown reflects the
 * CURRENT theme (sun while light, moon while dark); clicking flips it. */
const ICON_SUN = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="5"/><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>';
export const ICON_MOON = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>';

/** Inline vector wordmark — a fixed accent-green monogram badge + "STAFFLY"
 * text. SUPERSEDED as the rendered default brand mark (2026-07-15: the real
 * Staffly logo PNGs replaced this placeholder on the login card, the
 * loading/unauthorized interstitials, and the sidebar — see
 * loginBrandLogoHtml()/defaultLogoSrc() below) but kept exported/defined in
 * case anything still imports it (grep found none as of this change; not
 * deleted to avoid breaking an out-of-tree or not-yet-committed consumer). */
export const BRAND_MARK_SVG = '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><rect width="24" height="24" rx="7" fill="#2E8B73"/><path d="M8.3 15.9c.1 1.6 1.5 2.7 3.6 2.7 2.2 0 3.6-1.1 3.6-2.8 0-1.4-.8-2.2-2.6-2.6l-1.5-.3c-.8-.2-1.1-.5-1.1-1 0-.6.6-1.1 1.5-1.1.9 0 1.6.5 1.7 1.3h2c-.1-1.7-1.5-2.8-3.7-2.8-2.1 0-3.6 1.1-3.6 2.7 0 1.3.8 2.2 2.5 2.6l1.5.3c.9.2 1.3.5 1.3 1.1 0 .6-.6 1.1-1.7 1.1-1.1 0-1.8-.5-1.9-1.2H8.3z" fill="#fff"/></svg>';

/** Real Staffly logo markup for the pre-auth `.login-card` surfaces (loading
 * + unauthorized + platform-handoff interstitials here; login.js's own
 * sign-in card carries a literal copy of the same markup so it stays
 * independently mountable — see login.js's brandLogoHtml() comment).
 * THEME-AWARE (2026-07-21 fix): `.login-card` and friends used to be
 * hardcoded dark-only, so this was pinned to dark_logo.png; now that the
 * shared classes flip with html[data-theme] (tokens.css), the logo variant
 * must flip with them too — defaultLogoSrc() is the SAME resolver the
 * authenticated shell's sidebar uses. The PNG already bakes in the
 * "STAFFLY" wordmark, so no adjacent text label. */
function loginBrandLogoHtml() {
  return `<img src="${defaultLogoSrc()}" alt="" style="height:44px;width:auto;max-width:190px;object-fit:contain" />`;
}

/** Shown the instant mountShell is called, before getSessionBootstrap has
 * resolved — reuses the login screen's `#login-screen`/`.login-card` frame
 * (tokens.css) so there's no visual flash between this, the login card, and
 * (for admin/hr/manager) the real shell. No nav, no routes, no callables. */
function shellLoadingTemplate() {
  return `
    <div id="login-screen">
      <div class="login-card" style="text-align:center">
        <div class="login-brand">${loginBrandLogoHtml()}</div>
        <div class="login-tagline" style="margin-bottom:0">${escapeHtmlShell(t('shell.loadingWorkspace'))}</div>
      </div>
    </div>`;
}

/** Shown when getSessionBootstrap resolves with a DEFINITIVELY KNOWN role
 * outside the cockpit + employee-portal sets — a role this workspace has no
 * surface for. Copy is deliberately role-agnostic (2026-07-15 — the old
 * "for admins, HR, and managers" line went stale the moment employees/
 * supervisors/finance were let in). Offers a Sign out (not a dead end)
 * since the caller is stuck signed-in otherwise. */
function unauthorizedTemplate() {
  return `
    <div id="login-screen">
      <div class="login-card" style="text-align:center">
        <div class="login-brand">${loginBrandLogoHtml()}</div>
        <div class="login-platform-badge">${escapeHtmlShell(t('auth.portalBadge'))}</div>
        <div class="login-tagline" style="margin-bottom:24px">
          ${escapeHtmlShell(t('unauthorized.body'))}
        </div>
        <button type="button" class="btn btn-ghost" id="unauthorized-signout" style="width:100%;justify-content:center">${escapeHtmlShell(t('common.signOut'))}</button>
      </div>
    </div>`;
}

function wireUnauthorizedSignOut(root) {
  root.querySelector('#unauthorized-signout')?.addEventListener('click', () => signOut(auth));
}

/** Platform-account interstitial (2026-07-15): shown when the caller's ID
 * token POSITIVELY carries the platform-tier claim (see isPlatformAccount).
 * A platform operator has no tenant workspace here — hand them off to the
 * in-app Platform Ops screen (screens/superadmin.js, route /platform) on
 * their branded card instead of the stripped role=null cockpit. Same
 * always-dark `.login-card` frame as the other interstitials.
 *
 * The primary action is a <button>, not an `<a href>` — see
 * wirePlatformInterstitial's Q11 fix note: it mounts the shell in-app rather
 * than linking to the never-shipped external portal. */
function platformAccountTemplate() {
  return `
    <div id="login-screen">
      <div class="login-card" style="text-align:center">
        <div class="login-brand">${loginBrandLogoHtml()}</div>
        <div class="login-platform-badge">${escapeHtmlShell(t('platform.badge'))}</div>
        <div class="login-tagline" style="margin-bottom:8px">
          ${escapeHtmlShell(t('platform.title'))}
        </div>
        <div style="font-size:12.5px;color:var(--text-m);margin-bottom:24px">
          ${escapeHtmlShell(t('platform.body'))}
        </div>
        <button type="button" class="btn btn-primary" id="platform-open-superadmin"
           style="width:100%;justify-content:center;display:flex;box-sizing:border-box;margin-bottom:10px">${escapeHtmlShell(t('platform.openSuperadmin'))}</button>
        <button type="button" class="btn btn-ghost" id="platform-signout" style="width:100%;justify-content:center">${escapeHtmlShell(t('common.signOut'))}</button>
      </div>
    </div>`;
}

/**
 * wirePlatformInterstitial — Q11 fix (2026-07-24, superadmin-hidden-entry):
 * "Open Superadmin Portal" used to be a plain `<a href="https://zephyr-ops-
 * 7q3k.web.app">` — a Flutter portal that was never shipped (confirmed in
 * screens/superadmin.js's file header). The REAL in-app replacement
 * (screens/superadmin.js, route /platform, reached via wireSuperadminEntry's
 * hidden double-click-the-sidebar-logo affordance) only ever becomes
 * reachable once mountShellFrame() has run — it mounts shellTemplate()
 * (#content-region) and calls startRouter()/wireSuperadminEntry(). Every
 * platform-tier caller hit this interstitial's `return` in
 * attemptBootstrapAndMount BEFORE mountShellFrame ever ran, so the built
 * screen had zero live path to it — clicking the old link just left the app
 * (to a dead site) instead of reaching the working replacement.
 *
 * Fix: the button now mounts the same shell frame a role===null tenant
 * caller already falls back to (existing, already-exercised code path — see
 * the isPlatform===false branch just above this one in
 * attemptBootstrapAndMount) and navigates straight to /platform, where
 * screens/superadmin.js's own platform-tier re-check + every CF's
 * requirePlatformRole gate remain the real security boundary (unchanged).
 * `{ once: true }` guards against a double-click double-mounting the shell
 * (double startRouter() call / duplicate hashchange listener).
 */
function wirePlatformInterstitial(root, user) {
  root.querySelector('#platform-signout')?.addEventListener('click', () => signOut(auth));
  root.querySelector('#platform-open-superadmin')?.addEventListener('click', () => {
    mountShellFrame(root, user, null, false);
    navigate('/platform');
  }, { once: true });
}

/** Local HTML-escape for shell-chrome templates (mirrors pillar_home.js's
 * escapeHtml — shell.js had none because its templates were static until
 * the i18n'd interstitial copy landed). */
function escapeHtmlShell(text) {
  const div = document.createElement('div');
  div.textContent = text == null ? '' : String(text);
  return div.innerHTML;
}

function shellTemplate() {
  return `
    <div id="app-shell" class="visible">
      <div class="shell-body">
        <aside class="sidebar">
          <div class="sidebar-logo">
            <div style="display:flex;flex-direction:column;gap:3px;min-width:0;flex:1">
              <div style="display:flex;align-items:center;gap:8px;min-width:0">
                <div class="sidebar-brand" id="sidebar-logo-text"><img id="sidebar-logo-default" src="${defaultLogoSrc()}" alt="" style="height:26px;width:auto;max-width:130px;object-fit:contain" /></div>
                <img id="sidebar-logo-img" alt="" style="display:none;max-height:28px;max-width:140px;width:auto;height:auto;object-fit:contain" />
              </div>
              <div id="sidebar-company-name" style="font-size:11.5px;font-weight:600;color:var(--text-m);white-space:nowrap;overflow:hidden;text-overflow:ellipsis" hidden></div>
            </div>
            <button class="sidebar-close" id="sidebar-close" aria-label="${t('common.closeMenu')}">&times;</button>
          </div>
          <nav class="sidebar-nav" id="sidebar-nav"></nav>
          <div class="sidebar-user">
            <div class="user-avatar" id="sidebar-av">…</div>
            <div class="user-info">
              <div class="user-name" id="sidebar-name">Loading…</div>
              <div class="user-role" id="sidebar-role">—</div>
            </div>
          </div>
        </aside>
        <div class="sidebar-overlay" id="sidebar-overlay"></div>
        <div class="main-area">
          <div class="top-header">
            <button class="menu-btn" id="menu-btn" aria-label="${t('common.openMenu')}">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M3 12h18M3 6h18M3 18h18"/></svg>
            </button>
            <div class="page-title" id="page-title">${t('nav.screen.dashboard')} <span class="page-breadcrumb" id="page-breadcrumb">/ ${t('common.overview')}</span></div>
            <div class="header-search" id="header-search" role="button" tabindex="0" aria-label="${t('common.search')}" title="${t('common.search')} (Ctrl/⌘ K)">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
              <input id="header-search-input" type="text" readonly placeholder="${t('common.search')}" aria-label="${t('common.search')}" />
              <span class="kbd">⌘K</span>
            </div>
            <button type="button" class="header-icon-btn" id="header-notif-btn" title="${t('nav.screen.notifications')}" aria-label="${t('nav.screen.notifications')}">
              ${ICONS.notifications}
              <span class="notif-count" id="header-notif-count" hidden></span>
            </button>
            <button type="button" class="header-icon-btn" id="theme-toggle" aria-label="${t('common.switchToLightTheme')}">${ICON_MOON}</button>
            <button type="button" class="header-icon-btn header-lang-btn" id="lang-toggle" aria-label="${t('common.toggleLangDirection')}">AR</button>
            <div class="header-role-badge" id="header-role-badge">…</div>
            <div class="account-menu" id="account-menu">
              <button type="button" class="header-avatar" id="header-avatar" aria-haspopup="true" aria-label="${t('common.accountMenu')}">…</button>
              <div class="account-dropdown" id="account-dropdown">
                <div class="account-dropdown-header">
                  <div class="account-dropdown-name" id="account-menu-name">…</div>
                  <div class="account-dropdown-email" id="account-menu-email"></div>
                </div>
                <button type="button" class="account-dropdown-item" id="account-menu-profile">${t('common.profile')}</button>
                <div class="account-dropdown-sep"></div>
                <button type="button" class="account-dropdown-item danger" id="account-menu-signout">${t('common.signOut')}</button>
              </div>
            </div>
          </div>
          <div class="content" id="content-region"></div>
        </div>
      </div>
    </div>`;
}
